/**
 * The voicemail player (docs/plans/oncall-line-plan.md 5, 7.5, 7.7).
 *
 * GET /calls/:id/voicemail writes an audit row ("voicemail played") on every
 * read, so the audio is fetched only when the person presses Play, never
 * when the drawer opens and never by the poll. The bytes arrive as a Blob
 * (the server checks them against their SHA-256 first); the player plays an
 * object URL that is revoked when the call changes or the drawer closes.
 *
 * With responseType 'blob', a failed request's body is a Blob too, so the
 * error's code is read out of it before choosing the words.
 */
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Loader2, Play, Voicemail } from 'lucide-react';
import { getVoicemailAudio } from '../../api/oncallApi';
import { formatDurationWords } from '../../lib/format';
import { secondaryBtnCls } from '../../../ui/styles';

interface VoicemailPlayerProps {
  callId: string;
  durationSec: number | null;
}

type PlayerState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; url: string }
  | { kind: 'error'; message: string };

interface ErrorBody {
  code: string | null;
  error: string | null;
}

/** Read a Blob as text (FileReader: jsdom and older browsers lack Blob.text). */
function blobText(blob: Blob): Promise<string> {
  if (typeof (blob as Blob & { text?: () => Promise<string> }).text === 'function') {
    return (blob as Blob & { text: () => Promise<string> }).text();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/** The { error, code } of a failed blob request, whether its body is JSON or a Blob. */
export async function readBlobError(err: unknown): Promise<ErrorBody & { status: number | null }> {
  const response = (err as { response?: { status?: number; data?: unknown } } | null)?.response;
  const status = typeof response?.status === 'number' ? response.status : null;
  let data: unknown = response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      data = JSON.parse(await blobText(data));
    } catch {
      data = null;
    }
  }
  const body = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  return {
    status,
    code: typeof body.code === 'string' ? body.code : null,
    error: typeof body.error === 'string' ? body.error : null,
  };
}

/** The words for a voicemail that could not be played (never the storage's own message). */
export function voicemailErrorMessage(code: string | null, status: number | null): string {
  switch (code) {
    case 'ONCALL_NO_VOICEMAIL':
      return 'This call has no voicemail.';
    case 'ONCALL_VOICEMAIL_STORAGE_UNAVAILABLE':
      return 'Voicemail storage is not set up yet, so this recording cannot be played.';
    case 'ONCALL_VOICEMAIL_INTEGRITY_FAILED':
      return 'The stored recording failed its integrity check and was not played. Tell a line admin.';
    case 'ONCALL_NOT_FOUND':
      return 'This call is not on this site’s line.';
    default:
      if (status === null) return 'The voicemail could not be loaded. Check your connection and try again.';
      return 'The voicemail could not be loaded. Try again.';
  }
}

export default function VoicemailPlayer({ callId, durationSec }: VoicemailPlayerProps) {
  const [state, setState] = useState<PlayerState>({ kind: 'idle' });
  const urlRef = useRef<string | null>(null);
  const requestRef = useRef(0);

  // A new call (or unmount) revokes the old audio and forgets any request in flight.
  useEffect(() => {
    setState({ kind: 'idle' });
    return () => {
      requestRef.current += 1;
      if (urlRef.current && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    };
  }, [callId]);

  const load = async () => {
    const request = ++requestRef.current;
    setState({ kind: 'loading' });
    try {
      const blob = await getVoicemailAudio(callId);
      if (request !== requestRef.current) return;
      if (typeof URL.createObjectURL !== 'function') {
        setState({ kind: 'error', message: 'This browser cannot play the voicemail here.' });
        return;
      }
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setState({ kind: 'ready', url });
    } catch (err) {
      const { code, status } = await readBlobError(err);
      if (request !== requestRef.current) return;
      setState({ kind: 'error', message: voicemailErrorMessage(code, status) });
    }
  };

  const length = durationSec !== null && durationSec !== undefined ? formatDurationWords(durationSec) : null;

  return (
    <div className="space-y-2" data-testid="oncall-voicemail">
      <p className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
        <Voicemail className="h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
        Voicemail{length ? `, ${length}` : ''}
      </p>
      {state.kind === 'ready' ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio controls autoPlay src={state.url} className="w-full" data-testid="oncall-voicemail-audio">
          Your browser cannot play this recording.
        </audio>
      ) : (
        <button
          type="button"
          onClick={load}
          disabled={state.kind === 'loading'}
          className={`${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`}
          data-testid="oncall-voicemail-play"
        >
          {state.kind === 'loading' ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Play className="h-4 w-4" aria-hidden="true" />
          )}
          {state.kind === 'loading' ? 'Loading…' : 'Play voicemail'}
        </button>
      )}
      {state.kind === 'error' && (
        <p role="alert" className="flex items-start gap-1.5 text-sm text-red-700 dark:text-red-400" data-testid="oncall-voicemail-error">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          {state.message}
        </p>
      )}
      <p className="text-xs text-gray-500 dark:text-gray-400">Playing the voicemail is recorded in the audit log.</p>
    </div>
  );
}
